import { Component } from '@theme/component';
import { VariantSelectedEvent, VariantUpdateEvent } from '@theme/events';
import { morph, MORPH_OPTIONS } from '@theme/morph';
import { OverflowList } from '@theme/overflow-list';
import { yieldToMainThread, getViewParameterValue, ResizeNotifier } from '@theme/utilities';

const PRODUCT_INFORMATION_SELECTOR = '[data-testid="product-information"], .product-information';
const GALLERY_PICKER_SELECTOR = '.product-media-variant-slot variant-picker.variant-picker--gallery-slot';
const IMAGE_SWATCH_LEGEND_PATTERN = /color|colour|finish|pattern|style/i;

let stalePickerNormalizationQueued = false;
const productJsonCache = new Map();

function getProductInformationRoots(root = document) {
  if (!(root instanceof Element || root instanceof Document)) return [];

  if (root instanceof Element && root.matches(PRODUCT_INFORMATION_SELECTOR)) {
    return [root];
  }

  return Array.from(root.querySelectorAll(PRODUCT_INFORMATION_SELECTOR));
}

function normalizeStaleGalleryVariantPickers(root = document) {
  const productInformationRoots = getProductInformationRoots(root);

  productInformationRoots.forEach((productInformation) => {
    const productDetails = productInformation.querySelector('.product-details');
    if (!(productDetails instanceof HTMLElement)) return;

    let mainPicker = productDetails.querySelector('variant-picker:not(.variant-picker--gallery-slot)');
    const galleryPickers = Array.from(productInformation.querySelectorAll(GALLERY_PICKER_SELECTOR));

    if (!mainPicker && galleryPickers.length) {
      const galleryPicker = galleryPickers[0];
      const productForm = productDetails.querySelector('product-form-component');
      const fallbackAnchor =
        productDetails.querySelector('.product-form-buttons, buy-buttons, .quantity-rules, .quantity-label') ||
        productDetails.lastElementChild;

      galleryPicker.classList.remove('variant-picker--gallery-slot');
      galleryPicker.classList.add('spacing-style');
      galleryPicker.setAttribute('ref', 'mainVariantPicker');

      if (productForm instanceof HTMLElement) {
        productForm.before(galleryPicker);
      } else if (fallbackAnchor instanceof HTMLElement) {
        fallbackAnchor.before(galleryPicker);
      } else {
        productDetails.append(galleryPicker);
      }

      mainPicker = galleryPicker;
    }

    if (mainPicker instanceof HTMLElement) {
      mainPicker.classList.remove('variant-picker--gallery-slot');
      mainPicker.classList.add('spacing-style');
      mainPicker.setAttribute('ref', 'mainVariantPicker');
    }

    productInformation.querySelectorAll('.product-media-container--variant-picker').forEach((container) => {
      if (!productDetails.contains(container)) {
        container.remove();
      }
    });

    productInformation.querySelectorAll('.product-media-variant-slot').forEach((slot) => {
      if (productDetails.contains(slot)) return;

      const removableContainer = slot.closest('.product-media-container--variant-picker, slideshow-slide, li');
      if (removableContainer instanceof HTMLElement && !productDetails.contains(removableContainer)) {
        removableContainer.remove();
      } else {
        slot.remove();
      }
    });

    productInformation.querySelectorAll('variant-picker.variant-picker--gallery-slot').forEach((picker) => {
      if (productDetails.contains(picker)) {
        picker.classList.remove('variant-picker--gallery-slot');
        return;
      }

      const removableContainer = picker.closest('.product-media-container--variant-picker, slideshow-slide, li');
      if (removableContainer instanceof HTMLElement && !productDetails.contains(removableContainer)) {
        removableContainer.remove();
      } else {
        picker.remove();
      }
    });
  });
}

function queueStaleGalleryVariantPickerNormalization(root = document) {
  if (stalePickerNormalizationQueued) return;
  stalePickerNormalizationQueued = true;

  requestAnimationFrame(() => {
    stalePickerNormalizationQueued = false;
    normalizeStaleGalleryVariantPickers(root);
    enhanceVariantPickerImages(root);
  });
}

function getVariantPickers(root = document) {
  if (!(root instanceof Element || root instanceof Document)) return [];

  if (root instanceof Element && root.matches('variant-picker[data-product-url]')) {
    return [root];
  }

  return Array.from(root.querySelectorAll('variant-picker[data-product-url]'));
}

async function getProductJson(productUrl) {
  if (!productUrl) return null;

  if (!productJsonCache.has(productUrl)) {
    productJsonCache.set(
      productUrl,
      fetch(`${productUrl}.js`, { credentials: 'same-origin' })
        .then((response) => (response.ok ? response.json() : null))
        .catch(() => null)
    );
  }

  return productJsonCache.get(productUrl);
}

function getLegendLabel(fieldset) {
  const legend = fieldset.querySelector('legend');
  if (!legend) return '';

  const legendClone = legend.cloneNode(true);
  legendClone.querySelectorAll('.variant-option__swatch-value').forEach((node) => node.remove());
  return legendClone.textContent.trim();
}

function getOptionIndex(fieldset, fieldsets) {
  const indexedValue = Number(fieldset.dataset.fieldsetIndex);
  if (Number.isFinite(indexedValue)) return indexedValue;
  return fieldsets.indexOf(fieldset);
}

function getVariantOptionValue(variant, optionIndex) {
  if (Array.isArray(variant?.options) && variant.options[optionIndex] != null) {
    return String(variant.options[optionIndex]);
  }

  const optionKey = `option${optionIndex + 1}`;
  if (variant?.[optionKey] != null) {
    return String(variant[optionKey]);
  }

  return '';
}

function getVariantImageUrl(variant) {
  return variant?.featured_image?.src || variant?.featured_media?.preview_image?.src || variant?.image?.src || '';
}

function getCurrentSelections(fieldsets) {
  return fieldsets.map((fieldset) => {
    const checkedInput = fieldset.querySelector('input[type="radio"]:checked');
    if (checkedInput) return checkedInput.value;

    const selectedOption = fieldset.querySelector('select option[selected]');
    if (selectedOption instanceof HTMLOptionElement) return selectedOption.value;

    const select = fieldset.querySelector('select');
    if (select instanceof HTMLSelectElement) return select.value;

    return '';
  });
}

function variantMatchesSelections(variant, selectedValues, optionIndex) {
  return selectedValues.every((selectedValue, selectedIndex) => {
    if (!selectedValue || selectedIndex === optionIndex) return true;
    return getVariantOptionValue(variant, selectedIndex) === selectedValue;
  });
}

function addImageToSetMap(setMap, optionValue, imageUrl) {
  if (!optionValue || !imageUrl) return;

  if (!setMap.has(optionValue)) {
    setMap.set(optionValue, new Set());
  }

  setMap.get(optionValue).add(imageUrl);
}

function buildOptionImageMap(productJson, optionIndex, selectedValues, optionValues) {
  const contextMatchedImageSets = new Map();
  const fallbackImageSets = new Map();

  (productJson?.variants || []).forEach((variant) => {
    const optionValue = getVariantOptionValue(variant, optionIndex);
    const imageUrl = getVariantImageUrl(variant);

    if (!optionValue || !imageUrl) return;

    addImageToSetMap(fallbackImageSets, optionValue, imageUrl);

    if (variantMatchesSelections(variant, selectedValues, optionIndex)) {
      addImageToSetMap(contextMatchedImageSets, optionValue, imageUrl);
    }
  });

  const optionImageMap = new Map();
  const distinctImages = new Set();

  for (const optionValue of optionValues) {
    const preferredImageSet = contextMatchedImageSets.get(optionValue);
    const fallbackImageSet = fallbackImageSets.get(optionValue);
    const imageSet = preferredImageSet && preferredImageSet.size ? preferredImageSet : fallbackImageSet;

    if (!imageSet || imageSet.size !== 1) return null;

    const [imageUrl] = Array.from(imageSet);
    if (!imageUrl) return null;

    optionImageMap.set(optionValue, imageUrl);
    distinctImages.add(imageUrl);
  }

  if (optionImageMap.size < 2 || distinctImages.size < 2) return null;

  return optionImageMap;
}

function enhanceFieldsetWithImages(fieldset, optionImageMap) {
  const labels = Array.from(fieldset.querySelectorAll('label.variant-option__button-label'));
  if (!labels.length) return false;

  const labelValues = labels
    .map((label) => label.querySelector('input[type="radio"]')?.value || '')
    .filter(Boolean);

  if (!labelValues.length || labelValues.some((value) => !optionImageMap.has(value))) {
    return false;
  }

  fieldset.classList.remove('variant-option--equal-width-buttons', 'variant-option--natural-width-buttons');
  fieldset.classList.add('variant-option--swatches', 'variant-option--image-swatches');

  labels.forEach((label) => {
    const input = label.querySelector('input[type="radio"]');
    if (!(input instanceof HTMLInputElement)) return;

    const imageUrl = optionImageMap.get(input.value);
    if (!imageUrl) return;

    const textNode = label.querySelector('[data-key="variant-option-text"]');
    const labelText = (textNode?.textContent || input.value || '').trim();
    const pill = label.querySelector('[data-key="variant-option-pill"]');

    label.classList.add('variant-option__button-label--has-swatch', 'variant-option__button-label--has-variant-image');

    if (pill) pill.remove();
    if (textNode) textNode.remove();

    let swatch = label.querySelector('.swatch--forced-image');
    if (!(swatch instanceof HTMLElement)) {
      swatch = document.createElement('span');
      swatch.className = 'swatch swatch--variant-image swatch--unscaled swatch--forced-image';
      label.appendChild(swatch);
    }

    swatch.style.setProperty('--swatch-background', `url('${imageUrl}')`);
    swatch.style.setProperty('--swatch-focal-point', 'center');

    let hiddenText = label.querySelector('.variant-image-picker__visually-hidden');
    if (!(hiddenText instanceof HTMLElement)) {
      hiddenText = document.createElement('span');
      hiddenText.className = 'visually-hidden variant-image-picker__visually-hidden';
      label.appendChild(hiddenText);
    }

    hiddenText.textContent = labelText;
  });

  fieldset.dataset.variantImageEnhanced = 'true';
  return true;
}

async function enhanceVariantPickerImages(root = document) {
  const pickers = getVariantPickers(root);
  if (!pickers.length) return;

  await Promise.all(
    pickers.map(async (picker) => {
      const productJson = await getProductJson(picker.dataset.productUrl);
      if (!productJson || !Array.isArray(productJson.variants) || !productJson.variants.length) return;

      const fieldsets = Array.from(picker.querySelectorAll('fieldset.variant-option'));
      if (!fieldsets.length) return;

      const selectedValues = getCurrentSelections(fieldsets);

      fieldsets.forEach((fieldset) => {
        const optionIndex = getOptionIndex(fieldset, fieldsets);
        if (optionIndex < 0) return;

        const legendLabel = getLegendLabel(fieldset);
        const canRenderAsImageSwatches =
          fieldsets.length === 1 || IMAGE_SWATCH_LEGEND_PATTERN.test(legendLabel);

        if (!canRenderAsImageSwatches) return;

        const optionValues = Array.from(fieldset.querySelectorAll('input[type="radio"]'))
          .map((input) => input.value)
          .filter(Boolean);

        const optionImageMap = buildOptionImageMap(productJson, optionIndex, selectedValues, optionValues);
        if (!optionImageMap) return;

        enhanceFieldsetWithImages(fieldset, optionImageMap);
      });
    })
  );
}

if (typeof document !== 'undefined') {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => queueStaleGalleryVariantPickerNormalization(), {
      once: true,
    });
  } else {
    queueStaleGalleryVariantPickerNormalization();
  }

  const stalePickerObserver = new MutationObserver((mutations) => {
    const shouldNormalize = mutations.some((mutation) => {
      if (!mutation.addedNodes.length && !mutation.removedNodes.length) return false;

      return Array.from(mutation.addedNodes).some((node) => {
        if (!(node instanceof Element)) return false;

        return (
          node.matches?.(PRODUCT_INFORMATION_SELECTOR) ||
          node.matches?.('.product-media-variant-slot, .product-media-container--variant-picker, variant-picker') ||
          node.querySelector?.(
            `${PRODUCT_INFORMATION_SELECTOR}, .product-media-variant-slot, .product-media-container--variant-picker, variant-picker`
          )
        );
      });
    });

    if (shouldNormalize) {
      queueStaleGalleryVariantPickerNormalization();
    }
  });

  stalePickerObserver.observe(document.documentElement, {
    childList: true,
    subtree: true,
  });
}

/**
 * @typedef {object} VariantPickerRefs
 * @property {HTMLFieldSetElement[]} fieldsets - The fieldset elements.
 * @property {HTMLElement} [overflowList] - The overflow list element.
 */

/**
 * A custom element that manages a variant picker.
 *
 * @template {import('@theme/component').Refs} [TRefs=VariantPickerRefs]
 * @extends Component<TRefs>
 */
export default class VariantPicker extends Component {
  /** @type {string | undefined} */
  #pendingRequestUrl;

  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {number[][]} */
  #checkedIndices = [];

  /** @type {HTMLInputElement[][]} */
  #radios = [];

  #resizeObserver = new ResizeNotifier(() => this.updateVariantPickerCss());

  connectedCallback() {
    super.connectedCallback();
    queueStaleGalleryVariantPickerNormalization(this.closest('.shopify-section') || document);
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);

    fieldsets.forEach((fieldset) => {
      const radios = Array.from(fieldset?.querySelectorAll('input') ?? []);
      this.#radios.push(radios);

      const initialCheckedIndex = radios.findIndex((radio) => radio.dataset.currentChecked === 'true');
      if (initialCheckedIndex !== -1) {
        this.#checkedIndices.push([initialCheckedIndex]);
      }
    });

    this.addEventListener('change', this.variantChanged.bind(this));
    this.#resizeObserver.observe(this);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver.disconnect();
  }

  /**
   * Handles the variant change event.
   * @param {Event} event - The variant change event.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    const selectedOption =
      event.target instanceof HTMLSelectElement ? event.target.options[event.target.selectedIndex] : event.target;

    if (!selectedOption) return;

    this.updateSelectedOption(event.target);
    this.dispatchEvent(new VariantSelectedEvent({
      id: selectedOption.dataset.optionValueId ?? '',
    }));

    const isOnProductPage =
      this.dataset.templateProductMatch === 'true' &&
      !event.target.closest('product-card') &&
      !event.target.closest('quick-add-dialog');

    // Morph the entire main content for combined listings child products, because changing the product
    // might also change other sections depending on recommendations, metafields, etc.
    const currentUrl = this.dataset.productUrl?.split('?')[0];
    const newUrl = selectedOption.dataset.connectedProductUrl;
    const loadsNewProduct = isOnProductPage && !!newUrl && newUrl !== currentUrl;
    const isOnFeaturedProductSection = Boolean(this.closest('featured-product-information'));

    const morphElementSelector = loadsNewProduct
      ? 'main'
      : isOnFeaturedProductSection
      ? 'featured-product-information'
      : undefined;

    this.fetchUpdatedSection(this.buildRequestUrl(selectedOption), morphElementSelector);

    const url = new URL(window.location.href);

    const variantId = selectedOption.dataset.variantId || null;

    if (isOnProductPage) {
      if (variantId) {
        url.searchParams.set('variant', variantId);
      } else {
        url.searchParams.delete('variant');
      }
    }

    // Change the path if the option is connected to another product via combined listing.
    if (loadsNewProduct) {
      url.pathname = newUrl;
    }

    if (url.href !== window.location.href) {
      yieldToMainThread().then(() => {
        history.replaceState({}, '', url.toString());
      });
    }
  }

  /**
   * @typedef {object} FieldsetMeasurements
   * @property {HTMLFieldSetElement} fieldset
   * @property {number | undefined} currentIndex
   * @property {number | undefined} previousIndex
   * @property {number | undefined} currentWidth
   * @property {number | undefined} previousWidth
   */

  /**
   * Gets measurements for a single fieldset (read phase).
   * @param {number} fieldsetIndex
   * @returns {FieldsetMeasurements | null}
   */
  #getFieldsetMeasurements(fieldsetIndex) {
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
    const fieldset = fieldsets[fieldsetIndex];
    const checkedIndices = this.#checkedIndices[fieldsetIndex];
    const radios = this.#radios[fieldsetIndex];

    if (!radios || !checkedIndices || !fieldset) return null;

    const [currentIndex, previousIndex] = checkedIndices;

    return {
      fieldset,
      currentIndex,
      previousIndex,
      currentWidth: currentIndex !== undefined ? radios[currentIndex]?.parentElement?.offsetWidth : undefined,
      previousWidth: previousIndex !== undefined ? radios[previousIndex]?.parentElement?.offsetWidth : undefined,
    };
  }

  /**
   * Applies measurements to a fieldset (write phase).
   * @param {FieldsetMeasurements} measurements
   */
  #applyFieldsetMeasurements({ fieldset, currentWidth, previousWidth, currentIndex, previousIndex }) {
    if (currentWidth) {
      fieldset.style.setProperty('--pill-width-current', `${currentWidth}px`);
    } else if (currentIndex !== undefined) {
      fieldset.style.removeProperty('--pill-width-current');
    }

    if (previousWidth) {
      fieldset.style.setProperty('--pill-width-previous', `${previousWidth}px`);
    } else if (previousIndex !== undefined) {
      fieldset.style.removeProperty('--pill-width-previous');
    }
  }

  /**
   * Updates the fieldset CSS.
   * @param {number} fieldsetIndex - The fieldset index.
   */
  updateFieldsetCss(fieldsetIndex) {
    if (Number.isNaN(fieldsetIndex)) return;

    const measurements = this.#getFieldsetMeasurements(fieldsetIndex);
    if (measurements) {
      this.#applyFieldsetMeasurements(measurements);
    }
  }

  /**
   * Updates the selected option.
   * @param {string | Element} target - The target element.
   */
  updateSelectedOption(target) {
    if (typeof target === 'string') {
      const targetElement = this.querySelector(`[data-option-value-id="${target}"]`);

      if (!targetElement) throw new Error('Target element not found');

      target = targetElement;
    }

    if (target instanceof HTMLInputElement) {
      const fieldsetIndex = Number.parseInt(target.dataset.fieldsetIndex || '');
      const inputIndex = Number.parseInt(target.dataset.inputIndex || '');

      if (!Number.isNaN(fieldsetIndex) && !Number.isNaN(inputIndex)) {
        const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
        const fieldset = fieldsets[fieldsetIndex];
        const checkedIndices = this.#checkedIndices[fieldsetIndex];
        const radios = this.#radios[fieldsetIndex];

        if (radios && checkedIndices && fieldset) {
          // Clear previous checked states
          const [currentIndex, previousIndex] = checkedIndices;

          if (currentIndex !== undefined && radios[currentIndex]) {
            radios[currentIndex].dataset.previousChecked = 'false';
          }
          if (previousIndex !== undefined && radios[previousIndex]) {
            radios[previousIndex].dataset.previousChecked = 'false';
          }

          // Update checked indices array - keep only the last 2 selections
          checkedIndices.unshift(inputIndex);
          checkedIndices.length = Math.min(checkedIndices.length, 2);

          // Update the new states
          const newCurrentIndex = checkedIndices[0]; // This is always inputIndex
          const newPreviousIndex = checkedIndices[1]; // This might be undefined

          // newCurrentIndex is guaranteed to exist since we just added it
          if (newCurrentIndex !== undefined && radios[newCurrentIndex]) {
            radios[newCurrentIndex].dataset.currentChecked = 'true';
          }

          if (newPreviousIndex !== undefined && radios[newPreviousIndex]) {
            radios[newPreviousIndex].dataset.previousChecked = 'true';
            radios[newPreviousIndex].dataset.currentChecked = 'false';
          }

          this.updateFieldsetCss(fieldsetIndex);
        }
      }
      target.checked = true;
    }

    if (target instanceof HTMLSelectElement) {
      const newValue = target.value;
      const newSelectedOption = Array.from(target.options).find((option) => option.value === newValue);

      if (!newSelectedOption) throw new Error('Option not found');

      for (const option of target.options) {
        option.removeAttribute('selected');
      }

      newSelectedOption.setAttribute('selected', 'selected');
    }
  }

  /**
   * Builds the request URL.
   * @param {HTMLElement} selectedOption - The selected option.
   * @param {string | null} [source] - The source.
   * @param {string[]} [sourceSelectedOptionsValues] - The source selected options values.
   * @returns {string} The request URL.
   */
  buildRequestUrl(selectedOption, source = null, sourceSelectedOptionsValues = []) {
    // this productUrl and pendingRequestUrl will be useful for the support of combined listing. It is used when a user changes variant quickly and those products are using separate URLs (combined listing).
    // We create a new URL and abort the previous fetch request if it's still pending.
    let productUrl = selectedOption.dataset.connectedProductUrl || this.#pendingRequestUrl || this.dataset.productUrl;
    this.#pendingRequestUrl = productUrl;
    const params = [];
    const viewParamValue = getViewParameterValue();

    // preserve view parameter, if it exists, for alternative product view testing
    if (viewParamValue) params.push(`view=${viewParamValue}`);

    if (this.selectedOptionsValues.length && !source) {
      params.push(`option_values=${this.selectedOptionsValues.join(',')}`);
    } else if (source === 'product-card') {
      if (this.selectedOptionsValues.length) {
        params.push(`option_values=${sourceSelectedOptionsValues.join(',')}`);
      } else {
        params.push(`option_values=${selectedOption.dataset.optionValueId}`);
      }
    }

    // If variant-picker is a child of some specific sections, we need to append section_id=xxxx to the URL
    const SECTION_ID_MAP = {
      'quick-add-component': 'section-rendering-product-card',
      'swatches-variant-picker-component': 'section-rendering-product-card',
      'featured-product-information': this.closest('featured-product-information')?.id,
    };

    const closestSectionId = /** @type {keyof typeof SECTION_ID_MAP} | undefined */ (
      Object.keys(SECTION_ID_MAP).find((sectionId) => this.closest(sectionId))
    );

    if (closestSectionId) {
      if (productUrl?.includes('?')) {
        productUrl = productUrl.split('?')[0];
      }
      return `${productUrl}?section_id=${SECTION_ID_MAP[closestSectionId]}&${params.join('&')}`;
    }

    return `${productUrl}?${params.join('&')}`;
  }

  /**
   * Fetches the updated section.
   * @param {string} requestUrl - The request URL.
   * @param {string} [morphElementSelector] - The selector of the element to be morphed. By default, only the variant picker is morphed.
   */
  fetchUpdatedSection(requestUrl, morphElementSelector) {
    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    fetch(requestUrl, { signal: this.#abortController.signal })
      .then((response) => response.text())
      .then((responseText) => {
        this.#pendingRequestUrl = undefined;
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        // Defer is only useful for the initial rendering of the page. Remove it here.
        html.querySelector('overflow-list[defer]')?.removeAttribute('defer');

        const textContent = html.querySelector(`variant-picker script[type="application/json"]`)?.textContent;
        if (!textContent) return;

        let newProduct;

        if (morphElementSelector === 'main') {
          this.updateMain(html);
        } else if (morphElementSelector) {
          this.updateElement(html, morphElementSelector);
        } else {
          const { overflowList } = this.refs;
          const wasSwatchesExpanded =
            overflowList instanceof OverflowList && overflowList.getAttribute('disabled') === 'true';

          newProduct = this.updateVariantPicker(html);

          if (wasSwatchesExpanded) {
            const overflowListAfterMorph = overflowList;
            if (overflowListAfterMorph instanceof OverflowList) {
              overflowListAfterMorph.showAll();
            }
          }
        }

        // Dispatch for all paths so product-form-component can reset #variantChangeInProgress
        if (this.selectedOptionId) {
          this.dispatchEvent(
            new VariantUpdateEvent(JSON.parse(textContent), this.selectedOptionId, {
              html,
              productId: this.dataset.productId ?? '',
              newProduct,
            })
          );
        }
      })
      .catch((error) => {
        if (error.name === 'AbortError') {
          console.warn('Fetch aborted by user');
        } else {
          console.error(error);
        }
      });
  }

  /**
   * @typedef {Object} NewProduct
   * @property {string} id
   * @property {string} url
   */

  /**
   * Re-renders the variant picker.
   * @param {Document | Element} newHtml - The new HTML.
   * @returns {NewProduct | undefined} Information about the new product if it has changed, otherwise undefined.
   */
  updateVariantPicker(newHtml) {
    /** @type {NewProduct | undefined} */
    let newProduct;

    const newVariantPickerSource = newHtml.querySelector(this.tagName.toLowerCase());

    if (!newVariantPickerSource) {
      throw new Error('No new variant picker source found');
    }

    // For combined listings, the product might have changed, so update the related data attribute.
    if (newVariantPickerSource instanceof HTMLElement) {
      const newProductId = newVariantPickerSource.dataset.productId;
      const newProductUrl = newVariantPickerSource.dataset.productUrl;

      if (newProductId && newProductUrl && this.dataset.productId !== newProductId) {
        newProduct = { id: newProductId, url: newProductUrl };
      }

      this.dataset.productId = newProductId;
      this.dataset.productUrl = newProductUrl;
    }

    morph(this, newVariantPickerSource, {
      ...MORPH_OPTIONS,
      getNodeKey: (node) => {
        if (!(node instanceof HTMLElement)) return undefined;
        const key = node.dataset.key;
        return key;
      },
    });
    this.updateVariantPickerCss();
    queueStaleGalleryVariantPickerNormalization(this.closest('.shopify-section') || document);

    return newProduct;
  }

  updateVariantPickerCss() {
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);

    // Batch all reads first across all fieldsets to avoid layout thrashing
    const measurements = fieldsets.map((_, index) => this.#getFieldsetMeasurements(index)).filter((m) => m !== null);

    // Batch all writes after all reads
    for (const measurement of measurements) {
      this.#applyFieldsetMeasurements(measurement);
    }
  }

  /**
   * Re-renders the desired element.
   * @param {Document} newHtml - The new HTML.
   * @param {string} elementSelector - The selector of the element to re-render.
   */
  updateElement(newHtml, elementSelector) {
    const element = this.closest(elementSelector);
    const newElement = newHtml.querySelector(elementSelector);

    if (!element || !newElement) {
      throw new Error(`No new element source found for ${elementSelector}`);
    }

    morph(element, newElement);
    queueStaleGalleryVariantPickerNormalization(element.closest('.shopify-section') || document);
  }

  /**
   * Re-renders the entire main content.
   * @param {Document} newHtml - The new HTML.
   */
  updateMain(newHtml) {
    const main = document.querySelector('main');
    const newMain = newHtml.querySelector('main');

    if (!main || !newMain) {
      throw new Error('No new main source found');
    }

    morph(main, newMain);
    queueStaleGalleryVariantPickerNormalization(document);
  }

  /**
   * Gets the selected option.
   * @returns {HTMLInputElement | HTMLOptionElement | undefined} The selected option.
   */
  get selectedOption() {
    const selectedOption = this.querySelector('select option[selected], fieldset input:checked');

    if (!(selectedOption instanceof HTMLInputElement || selectedOption instanceof HTMLOptionElement)) {
      return undefined;
    }

    return selectedOption;
  }

  /**
   * Gets the selected option ID.
   * @returns {string | undefined} The selected option ID.
   */
  get selectedOptionId() {
    const { selectedOption } = this;
    if (!selectedOption) return undefined;
    const { optionValueId } = selectedOption.dataset;

    if (!optionValueId) {
      throw new Error('No option value ID found');
    }

    return optionValueId;
  }

  /**
   * Gets the selected options values.
   * @returns {string[]} The selected options values.
   */
  get selectedOptionsValues() {
    /** @type HTMLElement[] */
    const selectedOptions = Array.from(this.querySelectorAll('select option[selected], fieldset input:checked'));

    return selectedOptions.map((option) => {
      const { optionValueId } = option.dataset;

      if (!optionValueId) throw new Error('No option value ID found');

      return optionValueId;
    });
  }
}

if (!customElements.get('variant-picker')) {
  customElements.define('variant-picker', VariantPicker);
}
